import * as yup from 'yup';

/**
 * E36 · Values — the both-ends input codecs of the tools' mutations (plan 03 §2.1 #3): the phone (before it saves), the
 * portal and — as the shield's inputRule — the API run the SAME schema. Shape only; the business rules (the deal is the deck's,
 * the ranking is 3-10 of the very important, every sector rated, the marks 0-10, the VLQ's ten areas, who may rate with the
 * client) are the decider's, where they are binding and answer with the board's code in the person's language. `subject` is ABSENT
 * from the client's own commands on purpose (the caller is the subject); `performer` is never on the wire.
 *
 * The act's id is MINTED ON THE DEVICE (UUIDv7): a retried request is the store's duplicate and records nothing.
 */
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);

const uuidV7 = yup
  .string()
  .trim()
  .matches(
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
  )
  .required();

const slug = yup.string().trim().min(1).max(64).required();
const cardId = yup.string().trim().min(1).max(64).required();
const text = (max: number) => yup.string().trim().min(1).max(max).required();
const cardList = yup.array().of(cardId).max(200).required();

export const publishValuesDeckInputSchema = yup.object({
  deckId: slug,
  name: text(200),
  cards: yup
    .array()
    .of(
      yup
        .object({
          card: cardId,
          // a missing line is the decider's DECK_TRANSLATION_MISSING (in the member's words), not a generic "required"
          en: yup.string().max(500).defined(),
          ru: yup.string().max(500).defined(),
        })
        .required(),
    )
    .max(300)
    .required(),
  presets: yup
    .array()
    .of(
      yup
        .object({
          preset: slug,
          cards: yup.number().integer().min(0).max(300).required(),
          cardIds: yup.array().of(cardId).max(300).nullable(),
        })
        .required(),
    )
    .max(10)
    .required(),
  languages: yup
    .array()
    .of(yup.string().trim().min(2).max(8).required())
    .min(1)
    .max(5)
    .required(),
  licence: text(500),
  citation: text(1000),
});

export const startValuesSortInputSchema = yup.object({
  sortId: uuidV7,
  deckId: slug,
  preset: slug,
  cardOrder: cardList,
});

export const completeValuesSortInputSchema = yup.object({
  sortId: uuidV7,
  piles: yup
    .object({
      veryImportant: cardList,
      important: cardList,
      notImportant: cardList,
    })
    .required(),
  ranked: cardList,
});

export const configureValuesWheelInputSchema = yup.object({
  wheelId: uuidV7,
  preset: slug,
  // the count (2-12) and the duplicates are the decider's board codes
  sectors: yup
    .array()
    .of(yup.object({ sectorId: slug, name: text(80) }).required())
    .max(50)
    .required(),
});

const sectorRating = yup.object({
  sectorId: slug,
  // a whole number is the shape; the 0-10 range is the decider's WHEEL_MARK_RANGE
  // a line is welcome, not required: an area may be marked without one (yup's required() would refuse '')
  valueStatement: yup.string().trim().max(500).defined(),
  current: yup.number().integer().required(),
  ideal: yup.number().integer().required(),
});

export const rateValuesWheelInputSchema = yup.object({
  wheelId: uuidV7,
  ratingId: uuidV7,
  sectors: yup.array().of(sectorRating.required()).max(50).required(),
});

export const rateValuesWheelInSessionInputSchema = yup.object({
  subject: userRef.required(),
  wheelId: uuidV7,
  sectors: yup.array().of(sectorRating.required()).max(50).required(),
});

export const noteValuesObstacleInputSchema = yup.object({
  wheelId: uuidV7,
  sectorId: slug,
  obstacleId: uuidV7,
  obstacle: text(500),
  severity: yup.number().integer().required(),
});

export const commitToValuedActionInputSchema = yup.object({
  actionId: uuidV7,
  value: text(200),
  origin: yup
    .object({
      kind: yup.string().oneOf(['wheelSector', 'activity', 'value']).required(),
      ref: text(300),
    })
    .required(),
  // an empty action is the decider's VALUES_ACTION_EMPTY
  title: yup.string().trim().max(500).required(),
  dueAt: yup.date().required(),
});

export const completeValuedLivingQuestionnaireInputSchema = yup.object({
  vlqId: uuidV7,
  areas: yup
    .array()
    .of(
      yup
        .object({
          area: slug,
          importance: yup.number().integer().required(),
          consistency: yup.number().integer().required(),
        })
        .required(),
    )
    .max(30)
    .required(),
});

export const defineValueActivityInputSchema = yup.object({
  activityId: uuidV7,
  value: text(200),
  // an empty activity is the decider's VALUES_ACTIVITY_EMPTY
  title: yup.string().trim().max(500).required(),
  difficulty: yup.number().integer().required(),
  suggestedFrom: yup.string().trim().max(64).nullable(),
  source: yup
    .string()
    .trim()
    .oneOf(['own', 'fromWeek', 'library', 'windRosePlan', 'ai'])
    .nullable(),
});

export const requestActivitySuggestionsInputSchema = yup.object({
  requestId: uuidV7,
  value: text(200),
  valueText: yup.string().trim().max(300).required(),
  language: yup.string().trim().max(8).nullable(),
  count: yup.number().integer().nullable(),
});

export const linkRecordToValueInputSchema = yup.object({
  value: text(200),
  focusType: slug,
  focus: text(100),
});

export const unlinkRecordFromValueInputSchema = yup.object({
  linkId: yup.string().trim().min(1).max(64).required(),
});

// ── E53 · the wind rose ────────────────────────────────────────────────────────────────────────
// Shape only: a whole number per need and all eight present are the type's; 1-10 and the rest (a name, a behaviour for an alternative,
// a rated way before a choice, a chosen way before a plan) are the deciders' — in the person's words (ROSE_*), not a generic "invalid".
const roseNeedKeys = [
  'loveAcceptance',
  'empathyUnderstanding',
  'predictabilityReliability',
  'safety',
  'competence',
  'autonomy',
  'selfExpression',
  'spontaneity',
] as const;
const roseScores = yup
  .object(
    Object.fromEntries(
      roseNeedKeys.map((k) => [k, yup.number().integer().required()]),
    ),
  )
  .required();
const roseExamples = yup
  .object(
    Object.fromEntries(
      roseNeedKeys.map((k) => [k, yup.string().trim().max(500).nullable()]),
    ),
  )
  .nullable();
const roseInspiredBy = yup
  .object({
    kind: yup
      .string()
      .trim()
      .oneOf([
        'myPast',
        'someoneClose',
        'someoneFar',
        'bookOrFilm',
        'valuesList',
        'other',
      ])
      .required(),
    ref: yup.string().trim().max(200).nullable(),
  })
  .nullable();
const roseLines = (max: number, each: number) =>
  yup.array().of(yup.string().trim().max(each).defined()).max(max).required();
const roseStrategyId = uuidV7;
const roseKind = yup
  .string()
  .trim()
  .oneOf(['current', 'alternative'])
  .required();
const roseBasis = yup.string().trim().oneOf(['imagined', 'lived']).required();
const rosePlanItems = yup
  .array()
  .of(
    yup
      .object({
        what: yup.string().trim().max(500).defined(),
        withWhom: yup.string().trim().max(300).nullable(),
        resources: yup.string().trim().max(500).nullable(),
      })
      .required(),
  )
  .max(20)
  .required();

export const describeBehaviorStrategyInputSchema = yup.object({
  strategyId: roseStrategyId,
  // an empty name is the decider's ROSE_NAME_EMPTY
  name: yup.string().trim().max(200).defined(),
  kind: roseKind,
  // an alternative without a behaviour is the decider's ROSE_BEHAVIOR_REQUIRED
  behaviors: roseLines(10, 300),
  inspiredBy: roseInspiredBy,
});
export const describeBehaviorStrategyInSessionInputSchema =
  describeBehaviorStrategyInputSchema.shape({ subject: userRef.required() });
export const rateStrategyOnNeedsInputSchema = yup.object({
  ratingId: uuidV7,
  strategyId: roseStrategyId,
  scores: roseScores,
  examples: roseExamples,
  basis: roseBasis,
});
export const rateStrategyOnNeedsInSessionInputSchema = yup.object({
  subject: userRef.required(),
  strategyId: roseStrategyId,
  scores: roseScores,
  examples: roseExamples,
  basis: roseBasis,
});
export const chooseBehaviorStrategyInputSchema = yup.object({
  choiceId: uuidV7,
  strategyId: roseStrategyId,
  comparedWith: yup
    .array()
    .of(yup.string().trim().max(64).required())
    .max(10)
    .required(),
  value: yup.string().trim().max(200).defined(),
  beliefs: roseLines(10, 300),
  sharedBy: roseLines(10, 200),
});
export const chooseBehaviorStrategyInSessionInputSchema = yup.object({
  subject: userRef.required(),
  strategyId: roseStrategyId,
  comparedWith: yup
    .array()
    .of(yup.string().trim().max(64).required())
    .max(10)
    .required(),
  value: yup.string().trim().max(200).defined(),
  beliefs: roseLines(10, 300),
  sharedBy: roseLines(10, 200),
});
export const writeStrategyPlanInputSchema = yup.object({
  planId: uuidV7,
  strategyId: roseStrategyId,
  planWeek: rosePlanItems,
  planMonth: rosePlanItems,
  planYear: rosePlanItems,
});

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
const args = <T extends yup.AnyObjectSchema>(input: T) =>
  yup.object({ input: input.required() });

export const publishValuesDeckArgsSchema = args(publishValuesDeckInputSchema);
export const startValuesSortArgsSchema = args(startValuesSortInputSchema);
export const completeValuesSortArgsSchema = args(completeValuesSortInputSchema);
export const configureValuesWheelArgsSchema = args(
  configureValuesWheelInputSchema,
);
export const rateValuesWheelArgsSchema = args(rateValuesWheelInputSchema);
export const rateValuesWheelInSessionArgsSchema = args(
  rateValuesWheelInSessionInputSchema,
);
export const noteValuesObstacleArgsSchema = args(noteValuesObstacleInputSchema);
export const commitToValuedActionArgsSchema = args(
  commitToValuedActionInputSchema,
);
export const completeValuedLivingQuestionnaireArgsSchema = args(
  completeValuedLivingQuestionnaireInputSchema,
);
export const defineValueActivityArgsSchema = args(
  defineValueActivityInputSchema,
);
export const requestActivitySuggestionsArgsSchema = args(
  requestActivitySuggestionsInputSchema,
);
export const linkRecordToValueArgsSchema = args(linkRecordToValueInputSchema);
export const unlinkRecordFromValueArgsSchema = args(
  unlinkRecordFromValueInputSchema,
);

export const describeBehaviorStrategyArgsSchema = args(
  describeBehaviorStrategyInputSchema,
);
export const describeBehaviorStrategyInSessionArgsSchema = args(
  describeBehaviorStrategyInSessionInputSchema,
);
export const rateStrategyOnNeedsArgsSchema = args(
  rateStrategyOnNeedsInputSchema,
);
export const rateStrategyOnNeedsInSessionArgsSchema = args(
  rateStrategyOnNeedsInSessionInputSchema,
);
export const chooseBehaviorStrategyArgsSchema = args(
  chooseBehaviorStrategyInputSchema,
);
export const chooseBehaviorStrategyInSessionArgsSchema = args(
  chooseBehaviorStrategyInSessionInputSchema,
);
export const writeStrategyPlanArgsSchema = args(writeStrategyPlanInputSchema);

export const valuesValidators = {
  publishValuesDeckInputSchema,
  startValuesSortInputSchema,
  completeValuesSortInputSchema,
  configureValuesWheelInputSchema,
  rateValuesWheelInputSchema,
  rateValuesWheelInSessionInputSchema,
  noteValuesObstacleInputSchema,
  commitToValuedActionInputSchema,
  completeValuedLivingQuestionnaireInputSchema,
  defineValueActivityInputSchema,
  requestActivitySuggestionsInputSchema,
  linkRecordToValueInputSchema,
  unlinkRecordFromValueInputSchema,
  publishValuesDeckArgsSchema,
  startValuesSortArgsSchema,
  completeValuesSortArgsSchema,
  configureValuesWheelArgsSchema,
  rateValuesWheelArgsSchema,
  rateValuesWheelInSessionArgsSchema,
  noteValuesObstacleArgsSchema,
  commitToValuedActionArgsSchema,
  completeValuedLivingQuestionnaireArgsSchema,
  defineValueActivityArgsSchema,
  requestActivitySuggestionsArgsSchema,
  linkRecordToValueArgsSchema,
  unlinkRecordFromValueArgsSchema,
};

/**
 * E53 · the wind rose's codecs, annotated: `getValidators` returns every codec in the repo and TypeScript gave up serialising the inferred
 * type (TS7056) — a widened `AnyObjectSchema` per key keeps the keys and ends the problem.
 */
const windRoseSchemas = {
  describeBehaviorStrategyInputSchema,
  describeBehaviorStrategyInSessionInputSchema,
  rateStrategyOnNeedsInputSchema,
  rateStrategyOnNeedsInSessionInputSchema,
  chooseBehaviorStrategyInputSchema,
  chooseBehaviorStrategyInSessionInputSchema,
  writeStrategyPlanInputSchema,
  describeBehaviorStrategyArgsSchema,
  describeBehaviorStrategyInSessionArgsSchema,
  rateStrategyOnNeedsArgsSchema,
  rateStrategyOnNeedsInSessionArgsSchema,
  chooseBehaviorStrategyArgsSchema,
  chooseBehaviorStrategyInSessionArgsSchema,
  writeStrategyPlanArgsSchema,
};
export const windRoseValidators: Record<
  keyof typeof windRoseSchemas,
  yup.AnyObjectSchema
> = windRoseSchemas;
